import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import SummaryMarkdown from "./SummaryMarkdown";

// Toplantı Özeti — kontrol barında ikon+etiket buton + portal modal.
// Summarize'a basınca modal KAPANIR, özet arka planda hazırlanır (toast bilgilendirir);
// bitince e-posta + Geçmiş Özetler'de görünür. summaryApi = { setRecording, summarize, getSummary }.
type SummaryApi = {
  setRecording?: (roomId: string, on: boolean) => Promise<any>;
  summarize?: (roomId: string) => Promise<any>;
  getSummary?: (roomId: string) => Promise<any>;
  getRecordingState?: (roomId: string) => Promise<any>;
};
type Props = {
  summaryApi?: SummaryApi;
  roomId: string;
  t?: (k: string, fb?: string) => string;
  // Kayıt başladı/durdu → odadaki herkese (LiveKit data) haber ver. LiveKitVideoCall sağlar.
  onRecordingChange?: (on: boolean) => void;
};
type Toast = { kind: "processing" | "done" | "fail" | "recording" | "recstop"; msg?: string };

const unwrap = (r: any) => (r && r.data && r.data.result !== undefined ? r.data.result : (r && r.data) || r || null);

export default function MeetingSummary({ summaryApi, roomId, t, onRecordingChange }: Props) {
  const tr = (k: string, fb: string) => (t ? t(k, fb) : fb);
  const [open, setOpen] = useState(false);
  const [recording, setRecordingState] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  // Unmount sonrası setState/zamanlayıcı tetiklenmesini engelle (toplantıdan ani çıkış).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Map a backend error reason to a clear, distinct message (Fix 1).
  const errMsg = (d: any): string => {
    const raw = String((d && (d.message || d.error || d.err)) || "").toLowerCase();
    if (raw.includes("no_recording")) return tr("memeetErrNoRecording", "Bu odada kayıt yok. Önce “Transkripti kaydet”'i açın, konuşun, sonra özetleyin.");
    if (raw.includes("transcribe")) return tr("memeetErrTranscribe", "Kayıt var ama ses yazıya dökülemedi (çok kısa/sessiz olabilir).");
    if (raw.includes("summary_failed")) return tr("memeetErrSummary", "Özet üretilemedi, tekrar deneyin.");
    if (raw.includes("insufficient")) return tr("memeetInsufficient", "Yetersiz kredi.");
    if (raw.includes("deduction")) return tr("memeetErrBilling", "Ücretlendirme başarısız.");
    return tr("memeetSummaryFail", "Özet oluşturulamadı.");
  };

  useEffect(() => {
    if (!open || !roomId) return;
    // Mevcut özeti getir.
    summaryApi?.getSummary?.(roomId).then((r) => {
      const d = unwrap(r);
      if (d && d.summary) setSummary(String(d.summary));
    }).catch(() => {});
    // Kayıt durumunu sunucudan oku → checkbox gerçek durumu göstersin (Fix 3).
    summaryApi?.getRecordingState?.(roomId).then((r) => {
      const d = unwrap(r);
      if (d && typeof d.enabled !== "undefined") setRecordingState(!!d.enabled);
    }).catch(() => {});
  }, [open, roomId, summaryApi]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const toggleRecording = useCallback(async (on: boolean) => {
    if (!summaryApi?.setRecording) return;
    setRecordingState(on);
    try {
      await summaryApi.setRecording(roomId, on);
      onRecordingChange?.(on); // odadaki herkese (sonradan katılan da getRecordingState ile) bildir
      if (on) {
        // (D) Kutu işaretlenince: kullanıcılar bilgilendirildi → modal kendi kapanır,
        //     kayıt SUNUCUDA DEVAM EDER (kapanmak kaydı durdurmaz).
        setToast({ kind: "recording" });
        setTimeout(() => { if (mountedRef.current) setOpen(false); }, 900);
        setTimeout(() => { if (mountedRef.current) setToast((c) => (c && c.kind === "recording" ? null : c)); }, 6000);
      } else {
        setToast({ kind: "recstop" });
        setTimeout(() => { if (mountedRef.current) setToast((c) => (c && c.kind === "recstop" ? null : c)); }, 6000);
      }
    } catch { setRecordingState(!on); }
  }, [summaryApi, roomId, onRecordingChange]);

  // Modal kapanır + toast. Özet ~birkaç dakika sürer; backend ignore_user_abort ile devam eder.
  const doSummarize = useCallback(() => {
    if (!summaryApi?.summarize) return;
    setOpen(false);
    onRecordingChange?.(false); // özet = kayıt biter → odadakilere "kayıt durduruldu" bildir
    setToast({ kind: "processing" });
    summaryApi.summarize(roomId).then((r) => {
      const d = unwrap(r);
      if (d && d.summary) setToast({ kind: "done" });
      // Async backend: özet arka planda hazırlanıyor (processing) → "fail" DEĞİL, bilgilendirici "hazırlanıyor"
      else if (d && (d.processing || d.success)) setToast({ kind: "processing" });
      else setToast({ kind: "fail", msg: errMsg(d) });
    }).catch((e) => {
      setToast({ kind: "fail", msg: errMsg((e && e.response && (e.response.data?.result || e.response.data)) || e) });
    }).finally(() => {
      setTimeout(() => { if (mountedRef.current) setToast((cur) => (cur && cur.kind === "processing" ? cur : null)); }, 8000);
    });
  }, [summaryApi, roomId, t, onRecordingChange]);

  const copySummary = useCallback(() => {
    if (!summary) return;
    try { navigator.clipboard?.writeText(summary); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_) {}
  }, [summary]);

  if (!summaryApi) return null;

  const modal = open ? createPortal(
    <div className="ms-backdrop" onClick={() => setOpen(false)} role="dialog" aria-modal="true">
      <div className="ms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms-head">
          <span className="ms-title">{tr("memeetSummaryTitle", "Toplantı Özeti")}</span>
          <button className="ms-close" onClick={() => setOpen(false)} aria-label="×">×</button>
        </div>
        <div className="ms-body">
          <label className="ms-toggle">
            <input type="checkbox" checked={recording} onChange={(e) => toggleRecording(e.target.checked)} />
            <span>{tr("memeetRecordConsent", "Transkripti kaydet (katılımcı rızası)")}</span>
          </label>
          <p className="ms-hint">{tr("memeetSummaryHint", "Kutu açıkken toplantı sesi kaydedilir. “Özetle”ye basınca kayıt durur, ses yazıya dökülür ve özet e-posta + Geçmiş Özetler'e düşer. Sen çıksan da işlem sunucuda tamamlanır. Toplantı kendiliğinden biterse de otomatik özetlenir.")}</p>

          <button className="ms-action" onClick={doSummarize}>
            {tr("memeetSummarizeNow", "Toplantıyı Özetle ve kaydı bitir")}
          </button>

          {summary && (
            <div className="ms-result">
              <SummaryMarkdown source={summary} />
              <button className="ms-copy" onClick={copySummary}>
                {copied ? tr("memeetSummaryCopied", "Kopyalandı") : tr("memeetSummaryCopy", "Kopyala")}
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{MS_CSS}</style>
    </div>,
    document.body
  ) : null;

  const toastNode = toast ? createPortal(
    <div className={`ms-toast ms-toast-${toast.kind}`}>
      {toast.kind === "processing" && tr("memeetSummaryProcessing", "Özet hazırlanıyor… Bitince e-posta ve Geçmiş Özetler'de görünecek.")}
      {toast.kind === "done" && ("✓ " + tr("memeetSummaryReady", "Özet hazır — e-postanızda ve Geçmiş Özetler'de."))}
      {toast.kind === "fail" && (toast.msg || tr("memeetSummaryFail", "Özet oluşturulamadı."))}
      {toast.kind === "recording" && ("● " + tr("memeetRecStarted", "Kayıt başladı — odadaki herkes bilgilendirildi."))}
      {toast.kind === "recstop" && tr("memeetRecStopped", "Kayıt durduruldu.")}
      <style>{TOAST_CSS}</style>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button type="button" className="ms-launch-btn" title={tr("memeetSummary", "Özet")}
        aria-label={tr("memeetSummary", "Özet")} onClick={() => setOpen(true)}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 6h16M4 11h16M4 16h10" /><path d="M15.5 19l2 2 3.5-3.5" />
        </svg>
        <span>{tr("memeetSummary", "Özet")}</span>
      </button>
      {modal}
      {toastNode}
      <style>{LAUNCH_CSS}</style>
    </>
  );
}

const LAUNCH_CSS = `
.ms-launch-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 999px;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.14); color: #eaf0f6;
  font-size: 14px; font-weight: 600; cursor: pointer; transition: background .15s, border-color .15s; white-space: nowrap; }
.ms-launch-btn:hover { background: rgba(255,255,255,0.14); border-color: rgba(255,255,255,0.28); }
.ms-launch-btn svg { flex: 0 0 auto; }
@media (max-width: 720px) {
  .ms-launch-btn { padding: 0; width: 38px; height: 38px; justify-content: center; }
  .ms-launch-btn span { display: none; }
}
`;

const TOAST_CSS = `
.ms-toast { position: fixed; left: 50%; bottom: 28px; transform: translateX(-50%); z-index: 2147483600;
  max-width: 460px; padding: 13px 20px; border-radius: 12px; font-size: 13.5px; line-height: 1.45; font-weight: 600;
  box-shadow: 0 14px 44px rgba(0,0,0,0.5); animation: msToastIn .3s ease both; }
@keyframes msToastIn { from { opacity: 0; transform: translate(-50%, 14px); } to { opacity: 1; transform: translate(-50%, 0); } }
.ms-toast-processing { background: #14202c; color: #cfe3f5; border: 1px solid rgba(29,185,84,0.4); }
.ms-toast-done { background: #0f2a18; color: #b9f6cd; border: 1px solid rgba(30,215,96,0.5); }
.ms-toast-fail { background: #2a1414; color: #ffc9c9; border: 1px solid rgba(255,90,90,0.5); }
.ms-toast-recording { background: #2a1414; color: #ffd0d0; border: 1px solid rgba(255,72,72,0.55); }
.ms-toast-recstop { background: #1b2330; color: #cdd8e6; border: 1px solid rgba(160,180,210,0.35); }
`;

const MS_CSS = `
.ms-backdrop { position: fixed; inset: 0; background: rgba(4,7,12,0.62); backdrop-filter: blur(3px); z-index: 2147483000; display: flex; align-items: center; justify-content: center; padding: 20px; }
.ms-modal { width: min(580px, 94vw); max-height: 86vh; display: flex; flex-direction: column; overflow: hidden; background: #11161e; color: #eaf0f6; border: 1px solid rgba(255,255,255,0.12); border-radius: 18px; box-shadow: 0 24px 70px rgba(0,0,0,0.65); animation: msIn .25s ease both; }
@keyframes msIn { from { opacity: 0; transform: translateY(14px) scale(.98); } to { opacity: 1; transform: none; } }
.ms-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); }
.ms-title { font-size: 17px; font-weight: 700; }
.ms-close { background: none; border: none; color: #9aa6b2; font-size: 24px; line-height: 1; cursor: pointer; padding: 0 4px; }
.ms-close:hover { color: #fff; }
.ms-body { padding: 20px; overflow-y: auto; }
.ms-toggle { display: flex; align-items: flex-start; gap: 11px; font-size: 14.5px; line-height: 1.4; cursor: pointer; }
.ms-toggle input { width: 18px; height: 18px; margin-top: 1px; accent-color: #1db954; flex: 0 0 auto; cursor: pointer; }
.ms-hint { color: #94a3b2; font-size: 13px; line-height: 1.55; margin: 10px 0 18px; }
.ms-action { width: 100%; padding: 13px; border: none; border-radius: 12px; background: #1db954; color: #04130a; font-weight: 700; font-size: 15px; cursor: pointer; transition: filter .15s; }
.ms-action:hover { filter: brightness(1.06); }
.ms-result { margin-top: 18px; }
.ms-result .smd-root { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 16px; max-height: 42vh; overflow-y: auto; }
.ms-copy { margin-top: 12px; padding: 9px 18px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.2); background: transparent; color: #eaf0f6; cursor: pointer; font-size: 13.5px; }
.ms-copy:hover { background: rgba(255,255,255,0.09); }
`;
